import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  CalendarPlus,
  CheckCircle2,
  Circle,
  ExternalLink,
  MapPin,
  Video,
  XCircle,
} from 'lucide-react';
import { LEVEL_LABELS, SESSION_FORMAT_LABELS } from '@ready/shared';
import { ApiError, useApi, type MyEventState, type PublicEvent } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/me';
import { formatSessionTime } from '@/lib/dates';
import { Badge, Button, Card, ErrorText, Input } from '@/components/ui';
import { MarkdownContent } from '@/components/course/markdown';
import { NameForm } from '@/components/name-form';
import { ShareQuizCard } from '@/components/quiz/share-quiz';
import {
  certificateRequirement,
  eventKindLabel,
  formatsLabel,
  mapsUrl,
  seatsText,
} from '@/components/event/learner-labels';
import { OfferedBy } from '@/components/org-brand';

type Session = PublicEvent['sessions'][number];
type MySession = MyEventState['sessions'][number];

const primaryLink =
  'flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90';
const secondaryLink =
  'inline-flex min-h-10 items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted';

/** `/e/:slug`: a workshop or webinar for learners. */
export function EventPage() {
  const { slug = '' } = useParams();
  const api = useApi();
  const { user, loading } = useAuth();
  const details = useQuery({
    queryKey: ['publicEvent', slug],
    queryFn: () => api.publicEvent(slug),
    retry: false,
  });
  const mine = useQuery({
    queryKey: ['myEvent', slug],
    queryFn: () => api.myEvent(slug),
    enabled: Boolean(user),
    retry: false,
    // Join links and check-in open on a schedule.
    refetchInterval: 60_000,
  });

  if (details.isPending || loading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (details.isError) {
    // Org events are visible to members only: signed out, offer to sign in.
    if (
      !user &&
      details.error instanceof ApiError &&
      [401, 403, 404].includes(details.error.status)
    )
      return (
        <Card className="max-w-3xl">
          <p className="font-medium">Sign in to see this event</p>
          <p className="mt-1 text-sm text-muted-foreground">
            It may be for members of an organization. Sign in with your work or university email.
          </p>
          <Link
            to={`/signin?next=${encodeURIComponent(`/e/${slug}`)}`}
            className={`${primaryLink} mt-4 sm:w-auto`}
          >
            Sign in
          </Link>
        </Card>
      );
    return <ErrorText error={details.error} />;
  }
  const e = details.data;
  const my = mine.data ?? null;
  const share = e.cancelled ? null : (
    <ShareQuizCard
      kind="event"
      slug={slug}
      title={e.title}
      eventKind={e.kind}
      audience={e.audience}
      heading="Invite others"
      text={
        e.audience === 'public'
          ? `Share this ${e.kind} with friends, classmates and colleagues, or put up posters for notice boards and common areas.`
          : `Spread the word in your organization with printable posters for notice boards and common areas.`
      }
    />
  );

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:grid-rows-[auto_1fr]">
      <div className="min-w-0 space-y-6 lg:col-start-1 lg:row-start-1">
        {e.cancelled && (
          <div
            role="status"
            className="flex items-start gap-3 rounded-xl border-2 border-destructive/60 bg-destructive/10 p-4"
          >
            <XCircle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
            <div>
              <p className="font-semibold">This event was cancelled</p>
              <p className="mt-1 text-sm text-muted-foreground">
                The sessions below won't take place. Registered attendees were notified by email.
              </p>
            </div>
          </div>
        )}
        <EventHeader event={e} signedUp={Boolean(my?.status && my.status !== 'cancelled')} />
      </div>

      <aside className="flex min-w-0 flex-col gap-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
        <ActionCard slug={slug} event={e} my={my} signedIn={Boolean(user)} />
        {share && <div className="hidden lg:block">{share}</div>}
      </aside>

      <div className="flex min-w-0 flex-col gap-6 lg:col-start-1 lg:row-start-2">
        <SessionsCard slug={slug} event={e} my={my} />
        {e.description && (
          <Card title={`About this ${e.kind}`}>
            <MarkdownContent markdown={e.description} />
          </Card>
        )}
        {share && <div className="lg:hidden">{share}</div>}
      </div>
    </div>
  );
}

function EventHeader({
  event: e,
  signedUp = false,
}: {
  event: PublicEvent;
  /** Registered or waitlisted already: seat counts are beside the point. */
  signedUp?: boolean;
}) {
  const format = formatsLabel(e.sessions.map((s) => s.format));
  const seats = e.cancelled || !e.registrationOpen || signedUp ? null : seatsText(e.seatsLeft);
  return (
    <Card>
      <p className="text-sm font-medium text-primary">
        {eventKindLabel(e.kind, e.sessions.length)}
      </p>
      <h1 className="mt-1 text-2xl font-semibold">{e.title}</h1>
      {e.hosts && <p className="mt-1 text-sm text-muted-foreground">With {e.hosts}</p>}
      {e.offeredBy ? (
        <OfferedBy org={e.offeredBy} label="Hosted by" className="mt-2" />
      ) : (
        !e.hosts && <p className="mt-1 text-sm text-muted-foreground">Hosted by {e.hostName}</p>
      )}
      {e.summary && <p className="mt-3 text-muted-foreground">{e.summary}</p>}
      <ul className="mt-4 flex flex-wrap gap-2">
        <li>
          <Badge>{LEVEL_LABELS[e.level]}</Badge>
        </li>
        {format && (
          <li>
            <Badge>{format}</Badge>
          </li>
        )}
        <li>
          <Badge>Free</Badge>
        </li>
        {e.certificate && (
          <li>
            <Badge>Certificate of participation</Badge>
          </li>
        )}
        {seats && (
          <li>
            <Badge>{seats}</Badge>
          </li>
        )}
      </ul>
    </Card>
  );
}

// ─── Sessions ──────────────────────────────────────────────────────────

function SessionsCard({
  slug,
  event: e,
  my,
}: {
  slug: string;
  event: PublicEvent;
  my: MyEventState | null;
}) {
  const registered = my?.status === 'registered';
  if (e.sessions.length === 0) return null;
  return (
    <Card title={e.sessions.length === 1 ? 'When and where' : `${e.sessions.length} sessions`}>
      <ol className="divide-y divide-border">
        {e.sessions.map((s, i) => (
          <SessionItem
            key={s.id}
            slug={slug}
            index={i}
            count={e.sessions.length}
            session={s}
            mine={registered && !e.cancelled ? my?.sessions.find((m) => m.id === s.id) : undefined}
            eventZone={e.timeZone}
          />
        ))}
      </ol>
    </Card>
  );
}

function SessionItem({
  slug,
  index,
  count,
  session: s,
  mine,
  eventZone,
}: {
  slug: string;
  index: number;
  count: number;
  session: Session;
  mine?: MySession;
  eventZone: string;
}) {
  const past = new Date(s.endsAt).getTime() < Date.now();
  const local = formatSessionTime(s.startsAt, s.endsAt);
  const theirs = formatSessionTime(s.startsAt, s.endsAt, eventZone);
  const label = s.title ?? (count > 1 ? `Session ${index + 1}` : null);
  const place = [s.venue, s.address].filter(Boolean).join(', ');
  return (
    <li className={`py-4 first:pt-0 last:pb-0 ${past && !mine?.checkInOpen ? 'opacity-70' : ''}`}>
      <div className="flex items-start gap-3">
        {mine?.attended ? (
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-india-green" aria-label="Attended" />
        ) : (
          <Circle className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          {label && (
            <p className="font-medium">
              {label}
              {count > 1 && s.title && (
                <span className="font-normal text-muted-foreground"> · Session {index + 1}</span>
              )}
            </p>
          )}
          <p className={label ? 'text-sm' : 'font-medium'}>
            <time dateTime={s.startsAt}>{local}</time>
            {past && <span className="text-muted-foreground"> · Ended</span>}
          </p>
          {theirs !== local && (
            <p className="text-xs text-muted-foreground">{theirs} (event time)</p>
          )}
          <p className="text-sm text-muted-foreground">{SESSION_FORMAT_LABELS[s.format]}</p>
          {s.venue && (
            <p className="flex items-start gap-1.5 text-sm">
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              <a
                href={mapsUrl(place)}
                target="_blank"
                rel="noopener noreferrer"
                className="-my-2 py-2 hover:text-primary hover:underline"
              >
                {s.venue}
                {s.address && <span className="block text-muted-foreground">{s.address}</span>}
              </a>
            </p>
          )}
          {mine?.attended && <p className="text-sm font-medium text-india-green">Checked in</p>}
          {mine && !past && <JoinBlock session={s} mine={mine} />}
          {mine?.checkInOpen && !mine.attended && <CheckInForm slug={slug} sessionId={s.id} />}
          {mine?.googleCalendarUrl && count > 1 && new Date(s.startsAt).getTime() > Date.now() && (
            <a
              href={mine.googleCalendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="-my-2 inline-flex items-center gap-1.5 py-2 text-sm text-primary"
            >
              <CalendarPlus className="size-4" aria-hidden /> Google Calendar
            </a>
          )}
        </div>
      </div>
    </li>
  );
}

/** The meeting link for registered attendees: prominent from 30 minutes before. */
function JoinBlock({ session: s, mine }: { session: Session; mine: MySession }) {
  if (s.format === 'in_person' || !mine.meetingUrl) return null;
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border p-3">
      {mine.joinOpen ? (
        <a
          href={mine.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${primaryLink} sm:w-auto`}
        >
          <Video className="size-4" aria-hidden /> Join now
        </a>
      ) : (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Video className="size-4 shrink-0" aria-hidden /> Join opens 30 minutes before
        </p>
      )}
      <a
        href={mine.meetingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="-my-2 flex items-center gap-1.5 break-all py-2 text-xs text-muted-foreground hover:text-primary"
      >
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
        {mine.meetingUrl}
      </a>
      {mine.meetingDetails && (
        <p className="whitespace-pre-line text-xs text-muted-foreground">{mine.meetingDetails}</p>
      )}
    </div>
  );
}

function CheckInForm({ slug, sessionId }: { slug: string; sessionId: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const checkIn = useMutation({
    mutationFn: () => api.checkInToSession(slug, sessionId, code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['myEvent', slug] });
      void queryClient.invalidateQueries({ queryKey: ['certificates'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
  const id = `check-in-${sessionId}`;
  return (
    <form
      className="mt-2 rounded-lg border border-primary/60 bg-primary/5 p-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        checkIn.mutate();
      }}
    >
      <label htmlFor={id} className="block text-sm font-medium">
        Check in
      </label>
      <p className="text-xs text-muted-foreground">Enter the code your host shares.</p>
      <div className="mt-2 flex gap-2">
        <Input
          id={id}
          required
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          minLength={4}
          maxLength={8}
          pattern="[A-Za-z0-9]{4,8}"
          placeholder="Code"
          className="min-w-0 flex-1 uppercase tracking-widest sm:w-36 sm:flex-none"
          value={code}
          onChange={(ev) => setCode(ev.target.value)}
        />
        <Button type="submit" className="shrink-0" disabled={checkIn.isPending || !code.trim()}>
          {checkIn.isPending ? 'Checking…' : 'Check in'}
        </Button>
      </div>
      <ErrorText error={checkIn.error} />
    </form>
  );
}

// ─── Registration ──────────────────────────────────────────────────────

function ActionCard({
  slug,
  event: e,
  my,
  signedIn,
}: {
  slug: string;
  event: PublicEvent;
  my: MyEventState | null;
  signedIn: boolean;
}) {
  const requirement = e.certificate
    ? `${certificateRequirement(e.minSessions, e.sessions.length)} for a certificate of participation.`
    : null;
  return (
    <Card title={e.cancelled ? 'Cancelled' : 'Registration'}>
      <div className="space-y-4">
        {e.cancelled ? (
          <p className="text-sm text-muted-foreground">This event was cancelled.</p>
        ) : !signedIn ? (
          <SignedOutAction slug={slug} event={e} />
        ) : !my ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <SignedInAction slug={slug} event={e} my={my} />
        )}
        {my?.certificateCode && (
          <div className="space-y-2 rounded-lg bg-primary/10 p-3 text-sm">
            <p className="flex items-center gap-2 font-medium">
              <Award className="size-4 text-primary" aria-hidden /> Certificate earned
            </p>
            <div className="flex flex-wrap gap-x-4">
              <Link
                to={`/verify/${my.certificateCode}`}
                className="-my-2 py-2 font-medium text-primary"
              >
                View certificate
              </Link>
              <Link to="/certificates" className="-my-2 py-2 font-medium text-primary">
                Your certificates
              </Link>
            </div>
          </div>
        )}
        {requirement && !my?.certificateCode && !e.cancelled && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Award className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
            {requirement}
          </p>
        )}
      </div>
    </Card>
  );
}

function SignedOutAction({ slug, event: e }: { slug: string; event: PublicEvent }) {
  if (!e.registrationOpen)
    return <p className="text-sm text-muted-foreground">Registration is closed.</p>;
  return (
    <Link to={`/signin?next=${encodeURIComponent(`/e/${slug}`)}`} className={primaryLink}>
      Sign in to register
    </Link>
  );
}

function SignedInAction({
  slug,
  event: e,
  my,
}: {
  slug: string;
  event: PublicEvent;
  my: MyEventState;
}) {
  const api = useApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['myEvent', slug] });
    void queryClient.invalidateQueries({ queryKey: ['publicEvent', slug] });
    void queryClient.invalidateQueries({ queryKey: ['home'] });
    void queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };
  const register = useMutation({
    mutationFn: () => api.registerForEvent(slug),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelEventRegistration(slug),
    onSuccess: refresh,
  });
  const leave = (question: string) => {
    if (window.confirm(question)) cancel.mutate();
  };

  if (my.status === 'registered')
    return (
      <div className="space-y-4">
        <p className="flex items-center gap-2 font-medium text-india-green">
          <CheckCircle2 className="size-5" aria-hidden /> You're registered
        </p>
        <p className="text-sm text-muted-foreground">
          We'll email you a reminder the day before each session and again shortly before it starts.
          {e.sessions.some((s) => s.format !== 'in_person') &&
            ' The join link is on this page and in your calendar invite.'}
        </p>
        {my.calendarUrl && (
          <div className="space-y-2">
            <a href={my.calendarUrl} className={`${secondaryLink} w-full`}>
              <CalendarPlus className="size-4" aria-hidden /> Add to calendar (.ics)
            </a>
            <a
              href={my.calendarUrl.replace(/^https?:/, 'webcal:')}
              className="-my-2 block py-2 text-center text-sm text-primary"
            >
              Subscribe to updates in your calendar app
            </a>
            {e.sessions.length === 1 && my.sessions[0]?.googleCalendarUrl && (
              <a
                href={my.sessions[0].googleCalendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="-my-2 block py-2 text-center text-sm text-primary"
              >
                Add to Google Calendar
              </a>
            )}
          </div>
        )}
        {/* Once it has started, attendance counts: no more cancelling. */}
        {!e.cancelled && new Date(e.startsAt ?? 0).getTime() > Date.now() && (
          <Button
            variant="ghost"
            className="w-full"
            disabled={cancel.isPending}
            onClick={() =>
              leave(
                'Cancel your registration? Your seat goes to the next person on the waitlist, if there is one.',
              )
            }
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel registration'}
          </Button>
        )}
        <ErrorText error={cancel.error} />
      </div>
    );

  if (my.status === 'waitlisted')
    return (
      <div className="space-y-4">
        <p className="font-medium">
          You're #{my.waitlistPosition ?? '?'} on the waitlist.
          <span className="block font-normal text-muted-foreground">
            We'll email you if a seat opens up.
          </span>
        </p>
        <Button
          variant="secondary"
          className="w-full"
          disabled={cancel.isPending}
          onClick={() => leave('Leave the waitlist?')}
        >
          {cancel.isPending ? 'Leaving…' : 'Leave waitlist'}
        </Button>
        <ErrorText error={cancel.error} />
      </div>
    );

  if (!e.registrationOpen)
    return <p className="text-sm text-muted-foreground">Registration is closed.</p>;

  if (my.needsName)
    return me.data ? (
      <div className="space-y-2">
        <p className="text-sm font-medium">Your certificate shows this name</p>
        <p className="text-sm text-muted-foreground">
          Add your full name before registering: it's printed on your certificate of participation.
        </p>
        <NameForm me={me.data} submitLabel="Save and continue" onSaved={refresh} />
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">Loading…</p>
    );

  const full = e.seatsLeft !== null && e.seatsLeft <= 0;
  return (
    <div className="space-y-2">
      {full && (
        <p className="text-sm text-muted-foreground">
          Every seat is taken. Join the waitlist and we'll email you if one opens up.
        </p>
      )}
      <Button
        className="min-h-11 w-full"
        disabled={register.isPending}
        onClick={() => register.mutate()}
      >
        {register.isPending ? 'Registering…' : full ? 'Join the waitlist' : 'Register'}
      </Button>
      {seatsText(e.seatsLeft) && !full && (
        <p className="text-center text-xs text-muted-foreground">{seatsText(e.seatsLeft)}</p>
      )}
      <ErrorText error={register.error} />
    </div>
  );
}
